import { z } from 'zod';
import type { InterceptRule } from '../store/proxy-store';
import { getCurrentTenantId } from '../store/tenantStorage';

const InterceptRuleSchema = z.object({
  id: z.string(),
  name: z.string(),
  urlPattern: z.string(),
  methods: z.array(z.string()),
  enabled: z.boolean(),
  description: z.string().optional(),
});

const InterceptRulesStorageSchema = z.array(InterceptRuleSchema);

export function getInterceptRulesStorageKey({
  tenantId,
  applicationId,
}: {
  tenantId: string;
  applicationId: string;
}): string {
  return `terra-proxy-intercept-rules-${tenantId}-${applicationId}`;
}

export function loadInterceptRulesForApplication(
  applicationId: string,
): InterceptRule[] {
  if (typeof window === 'undefined' || !applicationId) {
    return [];
  }

  const tenantId = getCurrentTenantId();

  if (!tenantId) {
    return [];
  }

  const raw = localStorage.getItem(
    getInterceptRulesStorageKey({ tenantId, applicationId }),
  );

  if (!raw) {
    return [];
  }

  try {
    const parsed = InterceptRulesStorageSchema.parse(JSON.parse(raw));

    return parsed;
  } catch (error: unknown) {
    console.warn('Failed to parse stored intercept rules', {
      error: error instanceof Error ? error : new Error(String(error)),
      props: { applicationId, tenantId },
    });

    return [];
  }
}

export function saveInterceptRulesForApplication({
  applicationId,
  rules,
}: {
  applicationId: string;
  rules: InterceptRule[];
}): void {
  if (typeof window === 'undefined' || !applicationId) {
    return;
  }

  const tenantId = getCurrentTenantId();

  if (!tenantId) {
    throw new Error('Tenant is not set');
  }

  localStorage.setItem(
    getInterceptRulesStorageKey({ tenantId, applicationId }),
    JSON.stringify(rules),
  );
}
